<template>
  <section v-if="newEvent">
    <div class="event-form--section-title">
      <div class="section-title__tooltip">
        Zones
        <Tooltip position="left">
          <template v-slot:text>
            <IconInfo />
          </template>
          <template v-slot:tooltip>
            A zone consists of at least one sensor and represents a place of interest (PoI) for
            visitor insights at your event. Create a new zone for each PoI at your event.
          </template>
        </Tooltip>
      </div>
      List of available Zones in your event
    </div>
    <div class="event-form--content">
      <div v-if="newEvent.zones.length === 0" class="event-form--zones-empty">
        <IconZones />
        <strong>No Zones to Show yet</strong>
        <p>Create a zone to add sensor(s) and zone types</p>
        <AddZoneModal v-model:newEvent="newEvent" @onZoneAdded="onZoneAdded" />
      </div>
      <div v-else>
        <div class="event-form--content-top">
          <AddClusterModal
            v-model:newEvent="newEvent"
            :disabled="!checkedZones.length"
            :defaultZones="checkedZones"
            @onClusterAdded="onClusterAdded"
          />

          <AddZoneModal v-model:newEvent="newEvent" @onZoneAdded="onZoneAdded" />
        </div>

        <EditZoneModal
          v-if="editZone"
          v-model:editZone="editZone"
          v-model:newEvent="newEvent"
          :index="editZoneIndex"
          @onZoneEdited="onZoneEdited"
          @onZoneCanceled="onZoneCanceled"
        />

        <ul class="event-form--zones-list">
          <ZonePreview
            v-for="(zone, index) in newEvent.zones"
            :key="index"
            v-model:zone="newEvent.zones[index]"
            v-model:checkedZones="checkedZones"
            :canEdit="true"
            :index="index"
            :active="checkedZones.includes(zone)"
            @onRemoveZone="onRemoveZone(index)"
            @onEditZone="onEditZone"
          />
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import type { EventTypeNew } from '@/types/Event';
import type { ZoneType, ClusterType } from '@/types/Event';

import Tooltip from '@/components/global/Tooltip.vue';
import AddClusterModal from '@/components/dialogs/ClusterAdd.vue';
import AddZoneModal from '@/components/dialogs/ZoneAdd.vue';
import EditZoneModal from '@/components/dialogs/ZoneEdit.vue';
import ZonePreview from '@/components/events/ZonePreview.vue';

import IconInfo from '@/icons/IconInfo.svg?component';
import IconZones from '@/icons/events/IconZones.svg?component';

const newEvent = defineModel<EventTypeNew>('newEvent');
const emit = defineEmits(['onClusterAdded']);

const checkedZones = ref<ZoneType[]>([]);

const editZone = ref<ZoneType | null>();
const editZoneIndex = ref<number>(-1);

const onZoneAdded = (newZone: ZoneType) => {
  if (newEvent.value?.zones) {
    newEvent.value?.zones.push(newZone);
  }
};

const onEditZone = (zone: ZoneType, index: number) => {
  editZone.value = zone;
  editZoneIndex.value = index;
};

const onRemoveZone = (index: number) => {
  if (newEvent.value) {
    newEvent.value.zones.splice(index, 1);
  }
};

const onZoneCanceled = () => {
  editZone.value = null;
  editZoneIndex.value = -1;
};

const onZoneEdited = () => {
  editZone.value = null;
  editZoneIndex.value = -1;
};

const onClusterAdded = (newCluster: ClusterType) => {
  if (newEvent.value?.clusters) {
    newEvent.value.clusters.push(newCluster);
    emit('onClusterAdded');
  }
};
</script>
